import {Box, Button, ActionIcon, Flex, Text} from "@mantine/core";
import { useState, useEffect } from 'react';
import Fetch from '@/utils/Fetch';
import * as Enums from '@/utils/enums';
import Time from '@/utils/time';
import ManageForm from "@/components/modals/form/manage-form";
import DownloadService from '@/utils/download-service';
import helper from "@/utils/helper";
import SCDropdownList from "@/components/sc-controls/form-controls/sc-dropdownlist";
import SCSpinner from "@/components/sc-controls/misc/sc-spinner";
import useRefState from "@/hooks/useRefState";
import SCModal from "@/PageComponents/Modal/SCModal";
import styles from './form-definitions-for-item.module.css';
import {IconCircleChevronUp} from "@tabler/icons-react";
import {FormDefinition, FormHeader} from "@/interfaces/api/models";

interface Props {
    itemID: string;
    itemModule: number;
    customer?: any;
    supplier?: any;
    asset?: any;
    linkedFormDefinitions?: FormDefinition[];
    onClose: () => void;
    onlyLinkedForms?: boolean;
}

const FormDefinitionsForItem = ({ itemID, itemModule, customer, supplier, asset, linkedFormDefinitions, onClose, onlyLinkedForms = false }: Props) => {

    const [formHeaders, setFormHeaders] = useState<FormHeader[]>([]);
    const [formDefinitions, setFormDefinitions] = useState<FormDefinition[]>([]);
    const [manageFormHeader, setManageFormHeader] = useState<FormHeader | null>(null);
    const [selectedFormDefinition, setSelectedFormDefinition] = useState<FormDefinition | null>(null);
    const [isPrinting, setIsPrinting, getIsPrintingValue] = useRefState<FormHeader[]>([]);
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

    const getFormHeaders = async () => {
        let formHeaderResponse = await Fetch.get({
            url: "/Form/GetByItemID",
            params: {
                itemID: itemID,
                itemModule: itemModule
            }
        });

        setFormHeaders(formHeaderResponse.Results);
    };

    const getFormDefinitions = async () => {
        let formDefinitionResponse = await Fetch.post({
            url: "/FormDefinition/GetFormDefinitions",
            params: {
                PageSize: 100,
                ShowDraftVersion: true
            }
        });

        let results: FormDefinition[] = formDefinitionResponse.Results;
        if (itemModule === Enums.Module.Customer) {
            results = results.filter((x: FormDefinition) => x.FormRule === Enums.FormRule.Customer);
        }

        setFormDefinitions(results);
    };

    const printFormHeader = async (formHeader: FormHeader) => {
        let isPrintingTemp = [...getIsPrintingValue()];
        isPrintingTemp.push(formHeader);
        setIsPrinting(isPrintingTemp);
        DownloadService.downloadFile("GET", `/Form/PrintForm?formHeaderID=${formHeader.ID}`, null, true, false, "", "", null, false, () => {
            let isPrintingTemp = [...getIsPrintingValue()];
            isPrintingTemp = isPrintingTemp.filter((x: FormHeader) => x !== formHeader);
            setIsPrinting(isPrintingTemp);
        });
    };

    useEffect(() => {
        getFormDefinitions();
        getFormHeaders();
    }, []);

    const getAssociatedItemID = (module: number) => {
        switch (module) {
            case Enums.Module.Customer:
                return customer.ID;
            case Enums.Module.Asset:
                return asset.ID;
            case Enums.Module.Supplier:
                return supplier.ID;
            default:
                return null;
        }
    };

    const validForms = () => {
        let headersToShow: FormHeader[] = [...formHeaders];
        linkedFormDefinitions && linkedFormDefinitions.filter((x: FormDefinition) => x.IsActive).forEach((definition: FormDefinition) => {
            let idx = headersToShow.findIndex((x: FormHeader) => x.FormDefinition?.MasterID === definition.MasterID);
            if (idx < 0) {
                headersToShow.push({
                    FormDefinitionID: definition.ID,
                    FormDefinition: definition,
                    Module: itemModule,
                    ItemID: itemID,
                    FormStatus: Enums.FormStatus.Draft,
                    AssociatedModule: definition.Module,
                    AssociatedItemID: getAssociatedItemID(definition.Module!),
                    CompletedDate: null,
                    Invalid: false,
                    ID: helper.newGuid(),
                    _isNew: true
                });
            }
            else {
                headersToShow[idx]._isNew = false;
            }
        });

        return headersToShow.sort((a: FormHeader, b: FormHeader) => {
            return (a.FormDefinition?.Name || "") > (b.FormDefinition?.Name || "") ? 1 : -1;
        });
    };

    const unallocatedFormDefinitions = () => {
        let allocatedIDs = validForms().map((x: FormHeader) => x.FormDefinition?.MasterID);
        let unallocated = formDefinitions.filter((x: FormDefinition) => allocatedIDs.indexOf(x.MasterID) < 0);
        return unallocated.sort((a: FormDefinition, b: FormDefinition) => {
            return (a.Name || "") > (b.Name || "") ? 1 : -1;
        })
    }

    const getFormExpired = (formHeader: FormHeader) => {
        if (!formHeader.ExpireDate) {
            return false;
        }
        return Time.now().valueOf() > Time.parseDate(formHeader.ExpireDate).valueOf();
    };

    const getFormHeader = async (formHeaderID: string): Promise<FormHeader> => {
        const result = await Fetch.get({
            url: "/Form",
            params: {
                id: formHeaderID
            }
        });
        return result;
    };

    const doManageFormHeader = async (formHeader: FormHeader) => {
        if (!formHeader._isNew) {
            const result = await getFormHeader(formHeader.ID!);

            setManageFormHeader({
                ...result,
                _isNew: false
            });
        } else {
            setManageFormHeader({
                ...formHeader,
                _isNew: true
            });
        }
    };

    const onSave = async (formHeader: FormHeader | null = null) => {
        setManageFormHeader(formHeader);
        setSelectedFormDefinition(null);
        getFormDefinitions();
        getFormHeaders();
    };

    const onRenew = async (formHeader: FormHeader) => {
        setManageFormHeader(null);
        // // need timeout to let hooks apply new state
        setTimeout(() => {
            editSelectedFormHeader(formHeader);
        }, 100);
    };

    const renewExpiredForm = async (formHeader: FormHeader) => {
        doManageFormHeader(formHeader);
    };

    const editSelectedFormHeader = async (formHeader: FormHeader) => {
        let result = await getFormHeader(formHeader.ID!);
        result = {
            ...result,
            FormDefinition: formHeader.FormDefinition,
            Module: itemModule,
            ItemID: itemID,
            FormStatus: Enums.FormStatus.Draft,
            AssociatedModule: formHeader.FormDefinition?.Module,
            AssociatedItemID: getAssociatedItemID(formHeader.FormDefinition?.Module!),
            CompletedDate: null,
            Invalid: false,
            ExpireDate: null,
            _isNew: false
        };
        setManageFormHeader(result);
    };

    const addSelectedFormDefinition = (formDefinition: FormDefinition) => {
        const id = helper.newGuid();
        let formHeader: FormHeader = {
            ID: id,
            FormDefinitionID: formDefinition.ID,
            FormDefinition: formDefinition,
            Module: itemModule,
            ItemID: itemID,
            FormStatus: Enums.FormStatus.Draft,
            AssociatedModule: formDefinition.Module,
            AssociatedItemID: getAssociatedItemID(formDefinition.Module!),
            CompletedDate: null,
            Invalid: false,
            ExpireDate: null,
            FormDefinitionDisplayName: formDefinition.Name,
            FormDefinitionDescription: formDefinition.Description,
            _isNew: true
        };

        console.log(formHeader.ID, id);

        setManageFormHeader(formHeader);
    }

    const groupedForms = () => {
        const forms = validForms();
        const groups: { masterID: string, headers: FormHeader[] }[] = [];
        const indexByMasterID = new Map<string, number>();

        forms.forEach((fh) => {
            const masterID = fh.FormDefinition?.MasterID ?? fh.FormDefinitionID;
            if (masterID && indexByMasterID.has(masterID)) {
                groups[indexByMasterID.get(masterID)!].headers.push(fh);
            } else if (masterID) {
                indexByMasterID.set(masterID, groups.length);
                groups.push({ masterID, headers: [fh] });
            }
        });

        groups.forEach(group => {
            if (group.headers.length > 1) {
                group.headers.sort((a, b) => {
                    const aDate = a.CreatedDate ? new Date(a.CreatedDate).getTime() : 0;
                    const bDate = b.CreatedDate ? new Date(b.CreatedDate).getTime() : 0;
                    return aDate - bDate;
                });
            }
        });

        return groups;
    };

    const toggleGroup = (masterID: string) => {
        setCollapsedGroups(prev => {
            const next = new Set(prev);
            if (next.has(masterID)) next.delete(masterID); else next.add(masterID);
            return next;
        });
    };

    const renderRowActions = (formHeader: FormHeader, expired: boolean) => (
        <td className={styles.fitColumn}>
            {!formHeader._isNew ? (
                <span className={styles.imgContainer}>
                    {getIsPrintingValue().find((x) => x === formHeader) ? (
                        <SCSpinner colour="dark"/>
                    ) : (
                        <img className={styles.pointer} src="/icons/printer-blue.svg" alt="print"
                             title="Print form"
                             onClick={() => printFormHeader(formHeader)}/>
                    )}
                </span>
            ) : ''}
            {expired ? (
                <span className={styles.imgContainer}>
                    <img className={styles.pointer} src="/icons/repeat-blue.svg" alt="renew"
                         title="Renew expired form"
                         onClick={() => renewExpiredForm(formHeader)}/>
                </span>
            ) : ""}
            <span className={styles.imgContainer}>
                <img className={styles.pointer} src="/icons/edit.svg" alt="edit"
                     title={formHeader.FormStatus === Enums.FormStatus.Completed ? "View completed form" : "Edit draft form"}
                     onClick={() => doManageFormHeader(formHeader)}/>
            </span>
        </td>
    );

    const savedFormCount = validForms().filter((x) => !x._isNew).length;

    return (<>
    <SCModal open={!manageFormHeader}
             size={1100}
    >
        <Box mt={'sm'} className="title">
            <h1>Allocated Forms</h1>
        </Box>
        {savedFormCount > 0 && (
            <div className={styles.formCount}>{savedFormCount} form{savedFormCount !== 1 ? 's' : ''}</div>
        )}

        <div className={styles.tableContainer}>
            <table className={styles.table}>
                <thead>
                <tr>
                    <th>
                        <Flex align={'center'}>
                            <ActionIcon
                                variant="transparent"
                                color="dark"
                                style={{ visibility: 'hidden' }}
                            >
                                <IconCircleChevronUp size={16} />
                            </ActionIcon>
                            <span>Name</span>
                        </Flex>
                    </th>
                    <th>For</th>
                    <th>Version</th>
                    <th>Status</th>
                    <th>Expired</th>
                    <th className={styles.narrowColumn}></th>
                </tr>
                </thead>
                <tbody>
                {groupedForms().flatMap(({ masterID, headers }) => {
                    const isGroup = headers.length > 1;
                    const rows: JSX.Element[] = [];

                    if (headers.length === 1) {
                        const formHeader = headers[0];
                        const expired = getFormExpired(formHeader);
                        rows.push(
                            <tr key={formHeader.ID}>
                                <td>
                                    <Flex align={'center'}>
                                        <ActionIcon
                                            color={'dark'}
                                            variant="transparent"
                                            style={{ visibility: 'hidden' }}
                                        >
                                            <IconCircleChevronUp size={16} />
                                        </ActionIcon>
                                        <Text size={'sm'}>
                                            {formHeader.FormDefinition?.Name}
                                        </Text>
                                    </Flex>
                                </td>
                                <td>{Enums.getEnumStringValue(Enums.FormRule, formHeader.FormDefinition?.FormRule)}</td>
                                <td>{formHeader.FormDefinition?.Version}</td>
                                <td>{Enums.getEnumStringValue(Enums.FormStatus, formHeader.FormStatus)}</td>
                                <td>{expired ? <span className={styles.expired}>Yes</span> : "No"}</td>
                                {renderRowActions(formHeader, expired)}
                            </tr>
                        );
                    } else {
                        const parent = headers[0];
                        const copies = headers.slice(1);
                        const isCollapsed = collapsedGroups.has(masterID);
                        const parentExpired = getFormExpired(parent);

                        if (isGroup) {
                            rows.push(<tr key={`${masterID}-spacer-top`} className={styles.groupSpacerRow}><td colSpan={6}></td></tr>);
                        }

                        rows.push(
                            <tr key={`${masterID}-parent`} className={styles.groupParentRow}>
                                <td>
                                    <Flex align={'center'}>
                                        <ActionIcon
                                            variant="transparent"
                                            color="dark"
                                            onClick={() => toggleGroup(masterID)}
                                            aria-label={isCollapsed ? 'Expand copies' : 'Collapse copies'}
                                        >
                                            <IconCircleChevronUp
                                                className={`${styles.toggleIcon} ${!isCollapsed ? styles.toggleIconExpanded : ''}`}
                                                size={16}
                                            />
                                        </ActionIcon>
                                        <Text size={'sm'}>
                                            {parent.FormDefinition?.Name}
                                        </Text>
                                    </Flex>
                                </td>
                                <td>{Enums.getEnumStringValue(Enums.FormRule, parent.FormDefinition?.FormRule)}</td>
                                <td>{parent.FormDefinition?.Version}</td>
                                <td>{Enums.getEnumStringValue(Enums.FormStatus, parent.FormStatus)}</td>
                                <td>{parentExpired ? <span className={styles.expired}>Yes</span> : "No"}</td>
                                {renderRowActions(parent, parentExpired)}
                            </tr>
                        );

                        if (!isCollapsed) {
                            rows.push(...copies.map((copy, index) => {
                                const copyExpired = getFormExpired(copy);
                                return (
                                    <tr key={copy.ID} className={`${styles.copyRow} ${index % 2 === 0 ? styles.copyRowEven : styles.copyRowOdd}`}>
                                        <td className={styles.copyName}>
                                            {copy.FormDefinitionDisplayName || copy.FormDefinition?.Name}
                                        </td>
                                        <td></td>
                                        <td>{copy.FormDefinition?.Version}</td>
                                        <td>{Enums.getEnumStringValue(Enums.FormStatus, copy.FormStatus)}</td>
                                        <td>{copyExpired ? <span className={styles.expired}>Yes</span> : "No"}</td>
                                        {renderRowActions(copy, copyExpired)}
                                    </tr>
                                );
                            }));
                        }

                        if (isGroup) {
                            rows.push(<tr key={`${masterID}-spacer-bottom`} className={styles.groupSpacerRow}><td colSpan={6}></td></tr>);
                        }
                    }

                    return rows;
                })}
                </tbody>
            </table>
        </div>

        {!onlyLinkedForms && unallocatedFormDefinitions().length > 0 ? <>
            <div className={styles.row}>
                <div className={styles.column}>
                    <SCDropdownList
                        options={unallocatedFormDefinitions()}
                        label="Other forms"
                        onChange={setSelectedFormDefinition}
                        value={selectedFormDefinition}
                        dataItemKey="ID"
                        textField="Name"
                        canClear={true}
                    />
                </div>
                <div className={styles.column} style={{position: "relative"}}>
                    {selectedFormDefinition ? <div style={{position: "absolute", bottom: 0}}>
                        <Button onClick={() => addSelectedFormDefinition(selectedFormDefinition)}>Add Form</Button>
                    </div> : ""}
                </div>
            </div>
        </> : ""}

        <div>
            <div>
                <Button mt={'sm'} onClick={onClose} variant={'outline'}>
                    Close
                </Button>
            </div>
        </div>
    </SCModal>

    {manageFormHeader ? <>
        <ManageForm
            onSave={onSave}
            onRenew={onRenew}
            formHeaderToManage={manageFormHeader}
            isNew={manageFormHeader._isNew}
        />
    </> : ""}
    </>);
};

export default FormDefinitionsForItem;